<template>
  <v-container fluid>
    <h1 class="text-h5 font-weight-bold mb-6">Send Email Blast</h1>

    <!-- Sending progress modal (blocking) -->
    <v-dialog v-model="sending" persistent no-click-animation max-width="440">
      <v-card>
        <v-card-text class="text-center py-6">
          <v-progress-circular indeterminate color="primary" size="48" width="4" class="mb-4" />
          <div class="subtitle-1 font-weight-medium mb-4">Sending... ({{ progress.current }}/{{ progress.total }})</div>
          <v-alert type="warning" dense text class="mb-0 text-left">
            <div class="font-weight-bold mb-1">⚠️ กรุณาอย่าออกจากหน้านี้หรือปิดหน้าต่างจนกว่าการส่งจะเสร็จสิ้น</div>
            <div>Please do not leave this page or close the window until sending is complete.</div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Success result -->
    <v-card v-if="result" max-width="500">
      <v-card-title class="success--text">
        <v-icon color="success" left>mdi-check-circle</v-icon>
        Blast Sent!
      </v-card-title>
      <v-card-text>
        <div class="body-2">Total Targets: {{ result.totalTargets }}</div>
        <div class="body-2">Sent: {{ result.sentCount }}</div>
        <div v-if="result.failedCount > 0" class="body-2 error--text">Failed: {{ result.failedCount }}</div>

        <v-divider class="my-3" />
        <div class="subtitle-2 mb-1">Breakdown</div>
        <div v-for="b in result.blasts" :key="b.id" class="body-2">
          • {{ b.label }} — Blast #{{ b.id }} ({{ b.status }}), sent {{ b.sentCount
          }}<span v-if="b.failedCount > 0" class="error--text">, failed {{ b.failedCount }}</span>
        </div>
        <div v-for="(e, i) in result.errors" :key="`err-${i}`" class="body-2 error--text">
          • {{ e.label }} — failed: {{ e.message }}
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="result = null">Send Another</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Send form -->
    <v-card v-else max-width="700">
      <v-card-text>
        <v-alert v-if="error" type="error" dense class="mb-4">{{ error }}</v-alert>

        <!-- Target -->
        <div class="subtitle-2 mb-2">Target *</div>
        <v-radio-group v-model="targetMode" row class="mt-0 mb-4">
          <v-radio label="Organization IDs" value="org" />
          <v-radio label="Specific User IDs" value="users" />
        </v-radio-group>

        <v-text-field
          v-if="targetMode === 'org'"
          v-model="orgIds"
          label="Organization IDs (comma-separated) *"
          :rules="[rules.requiredWhenActive]"
          outlined
          dense
          class="mb-2"
          placeholder="1,2,3"
        />
        <v-text-field
          v-else
          v-model="userIds"
          label="User IDs (comma-separated) *"
          outlined
          dense
          class="mb-2"
          placeholder="1,2,3"
        />

        <!-- Subject -->
        <v-text-field
          v-model="subject"
          label="Subject *"
          :rules="[rules.required]"
          outlined
          dense
          class="mb-2"
          placeholder="Email subject"
        />

        <!-- Content mode -->
        <div class="subtitle-2 mb-2">Content *</div>
        <v-radio-group v-model="mode" row class="mt-0 mb-4">
          <v-radio label="Use Template" value="template" />
          <v-radio label="Custom HTML" value="html" />
        </v-radio-group>

        <template v-if="mode === 'template'">
          <v-select
            v-model="templateId"
            :items="activeTemplates"
            item-text="displayLabel"
            item-value="id"
            label="Template *"
            outlined
            dense
            class="mb-2"
          />
          <v-textarea
            v-model="variables"
            label="Variables (JSON, optional)"
            outlined
            dense
            rows="3"
            class="mb-2"
            style="font-family: monospace"
            placeholder='{"name": "John", "code": "ABC123"}'
          />
        </template>

        <v-textarea
          v-else
          v-model="htmlContent"
          label="HTML Content *"
          outlined
          dense
          rows="10"
          class="mb-2"
          style="font-family: monospace"
          placeholder="<h1>Hello</h1><p>Your email content here</p>"
        />

        <!-- From / Reply-To -->
        <v-row>
          <v-col>
            <v-text-field v-model="from" label="From" outlined dense placeholder="help@ooca.co" />
          </v-col>
          <v-col>
            <v-text-field v-model="replyTo" label="Reply-To" outlined dense placeholder="optional" />
          </v-col>
        </v-row>

        <v-btn color="primary" :loading="sending" :disabled="sending" @click="handleSend"> Send Blast </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmailBlastSend',

  data() {
    return {
      templates: [],
      mode: 'template',
      templateId: null,
      variables: '',
      htmlContent: '',
      subject: '',
      targetMode: 'org',
      userIds: '',
      orgIds: '',
      from: 'help@ooca.co',
      replyTo: '',
      sending: false,
      progress: { current: 0, total: 0 },
      result: null,
      error: null,
      rules: {
        required: (v) => !!v || 'This field is required',
        requiredWhenActive: (v) => !!v || 'This field is required',
      },
    };
  },

  computed: {
    activeTemplates() {
      return this.templates
        .filter((t) => t.active)
        .map((t) => ({
          ...t,
          displayLabel: `${t.name} (default subject: ${t.subject})`,
        }));
    },
  },

  created() {
    this.fetchTemplates();
  },

  methods: {
    async fetchTemplates() {
      try {
        const { data } = await axios.get('/v1/email-blast/templates');
        this.templates = data;
      } catch (err) {
        console.error('Failed to load templates', err);
      }
    },

    async handleSend() {
      this.error = null;

      if (!this.subject) {
        this.error = 'Subject is required.';
        return;
      }
      if (this.targetMode === 'org' && !this.orgIds.trim()) {
        this.error = 'At least one Organization ID is required.';
        return;
      }
      if (this.targetMode === 'users' && !this.userIds.trim()) {
        this.error = 'At least one User ID is required.';
        return;
      }
      if (this.mode === 'template' && !this.templateId) {
        this.error = 'Please select a template.';
        return;
      }
      if (this.mode === 'html' && !this.htmlContent.trim()) {
        this.error = 'HTML content is required.';
        return;
      }

      let parsedVars;
      if (this.mode === 'template' && this.variables.trim()) {
        try {
          parsedVars = JSON.parse(this.variables);
        } catch {
          this.error = 'Variables must be valid JSON, e.g. {"name": "John"}';
          return;
        }
      }

      const basePayload = {
        subject: this.subject,
        from: this.from || undefined,
        reply_to: this.replyTo || undefined,
      };

      if (this.mode === 'template') {
        basePayload.template_id = Number(this.templateId);
        if (parsedVars) basePayload.variables = parsedVars;
      } else {
        basePayload.html_content = this.htmlContent;
      }

      // Build the list of requests. Org mode sends one request per organization
      // so the frontend can loop and aggregate the results.
      let requests = [];
      if (this.targetMode === 'users') {
        const userIds = this.userIds
          .split(',')
          .map((s) => Number(s.trim()))
          .filter(Boolean);
        if (!userIds.length) {
          this.error = 'At least one valid User ID is required.';
          return;
        }
        requests = [{ payload: { ...basePayload, user_ids: userIds }, label: `Users (${userIds.length})` }];
      } else {
        const orgIds = this.orgIds
          .split(',')
          .map((s) => Number(s.trim()))
          .filter(Boolean);
        if (!orgIds.length) {
          this.error = 'At least one valid Organization ID is required.';
          return;
        }
        requests = orgIds.map((id) => ({ payload: { ...basePayload, organization_id: id }, label: `Org ${id}` }));
      }

      this.progress = { current: 0, total: requests.length };
      this.sending = true;

      const blasts = [];
      const errors = [];
      try {
        // Loop sequentially so the backend handles one blast at a time and the
        // modal can show accurate progress.
        for (let i = 0; i < requests.length; i += 1) {
          this.progress.current = i + 1;
          const { payload, label } = requests[i];
          try {
            // eslint-disable-next-line no-await-in-loop
            const { data } = await axios.post('/v1/email-blast/send', payload);
            blasts.push({ ...data, label });
          } catch (err) {
            errors.push({
              label,
              message:
                (err.response && err.response.data && err.response.data.message) ||
                err.message ||
                'Failed to send blast',
            });
          }
        }

        if (!blasts.length) {
          this.error = errors.map((e) => `${e.label}: ${e.message}`).join(' | ') || 'Failed to send blast';
          return;
        }

        this.result = {
          blasts,
          errors,
          totalTargets: blasts.reduce((sum, b) => sum + (b.totalTargets || 0), 0),
          sentCount: blasts.reduce((sum, b) => sum + (b.sentCount || 0), 0),
          failedCount: blasts.reduce((sum, b) => sum + (b.failedCount || 0), 0),
        };
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
